import type { AuthUser, PermissionScopeAccess } from '@/features/auth/auth';

// Scopes whose resources exist purely at the platform level (cross-tenant).
// Mirrors services/platform-service/app/auth/scopes.py:PLATFORM_SCOPES.
// Tenant admins are NOT granted these even via the implicit-bypass below;
// the platform-service backend rejects them at request time regardless.
const PLATFORM_SCOPES = new Set<string>([
  'tenants',
  'products',
  'operators',
  'entitlements',
]);

// Duplicated from `@/lib/auth/roles` rather than imported, to avoid a
// circular dependency (roles.ts imports `hasPermission` from this module).
// Both read the same stored `userType`, so they cannot disagree.
const isSuper = (user: AuthUser) => user.userType === 'superuser';
const isTenantAdmin = (user: AuthUser) => user.userType === 'admin';

export type PermissionAction =
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  // Granular device-lifecycle actions (device_units scope). The literals
  // match `DeviceLifecycleAction` in `./roles` so a device action can be
  // passed straight through to `hasPermission`.
  | 'transfer'
  | 'updateExpiration'
  | 'issueOfflinePin'
  | 'syncState'
  | 'resetState'
  | 'processReturn'
  | 'writeOff'
  | 'lock'
  | 'unlock'
  | 'removeLock'
  // Application review (applications scope).
  | 'approve'
  | 'reject'
  // Identity attestation — reachable from both applications and customers.
  | 'verifyKyc'
  // Contract lifecycle (contracts scope).
  | 'activate'
  // Withdrawal — reachable from both contracts and applications.
  | 'cancel'
  // Reverse a defaulted contract (contracts scope).
  | 'reinstate'
  // Reverse a recorded cash payment (payments scope).
  | 'reverse';

const ACTION_KEY: Record<PermissionAction, keyof PermissionScopeAccess> = {
  read: 'canRead',
  create: 'canCreate',
  update: 'canUpdate',
  delete: 'canDelete',
  transfer: 'canTransfer',
  updateExpiration: 'canUpdateExpiration',
  issueOfflinePin: 'canIssueOfflinePin',
  syncState: 'canSyncState',
  resetState: 'canResetState',
  processReturn: 'canProcessReturn',
  writeOff: 'canWriteOff',
  lock: 'canLock',
  unlock: 'canUnlock',
  removeLock: 'canRemoveLock',
  approve: 'canApprove',
  reject: 'canReject',
  verifyKyc: 'canVerifyKyc',
  activate: 'canActivate',
  cancel: 'canCancel',
  reinstate: 'canReinstate',
  reverse: 'canReverse',
};

export function hasPermission(
  user: AuthUser | null | undefined,
  scope: string,
  action: PermissionAction
): boolean {
  if (!user) return false;
  // SuperAdmin: cross-tenant bypass on every scope.
  if (isSuper(user)) return true;
  // TenantAdmin: implicit access to every tenant-scoped resource within their
  // tenant. Mirrors UserContext.is_tenant_super on the backend, which makes
  // the structured permissions list unnecessary for this role. Platform-only
  // scopes are excluded — they exist cross-tenant and would be rejected by
  // platform-service at request time anyway.
  if (isTenantAdmin(user) && !PLATFORM_SCOPES.has(scope)) return true;
  const entry = user.permissions.find((p) => p.scope === scope);
  if (!entry) return false;
  return Boolean(entry[ACTION_KEY[action]]);
}
